import React, { useEffect, useState } from "react";
import { api, formatZAR, formatDate } from "../api";
import { RenewalBadge } from "../components/StatusBadge";
import { BellRing } from "lucide-react";
import BusinessFilter from "../components/BusinessFilter";
import { useBusiness } from "../context/BusinessContext";

export default function Renewals() {
  const { business } = useBusiness();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [cats, setCats] = useState([]);
  const [window, setWindow] = useState(30);

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data));
    api.get("/task-categories").then(r => setCats(r.data));
  }, []);

  useEffect(() => {
    const params = { window_days: window };
    if (business) params.business = business;
    api.get("/renewals", { params }).then(r => setItems(r.data));
  }, [window, business]);

  const clientName = id => clients.find(c => c.id === id)?.name || "—";
  const catName = id => cats.find(c => c.id === id)?.name || "—";

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="np-label mb-2">Reminders</div>
          <h1 className="text-3xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>Annual renewal reminders</h1>
          <p className="text-[#595959] mt-2">Annual tasks due within the chosen window. Invoice early to give yourself buffer.</p>
        </div>
        <div className="np-card !p-3"><BusinessFilter /></div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span className="np-label">Window</span>
        {[30, 60, 90].map(w => (
          <button
            key={w}
            data-testid={`window-${w}`}
            onClick={() => setWindow(w)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
              window === w ? "bg-[#00ACB3] text-white" : "bg-white text-[#595959] border border-[#CCCCCC]"
            }`}
          >{w} days</button>
        ))}
      </div>

      <div className="np-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-[#011617]" style={{ fontFamily: "Manrope" }}>
            {items.length} {items.length === 1 ? "renewal" : "renewals"} in next {window} days
          </h2>
          <BellRing className="w-5 h-5 text-[#0F4F51]" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="np-table-header">
              <tr>
                <th className="text-left px-3 py-2.5">Next due</th>
                <th className="text-left px-3 py-2.5">Days</th>
                <th className="text-left px-3 py-2.5">Client</th>
                <th className="text-left px-3 py-2.5">Task</th>
                <th className="text-left px-3 py-2.5">Notes</th>
                <th className="text-right px-3 py-2.5">Price</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="text-center text-[#595959] py-8">No annual renewals coming up.</td></tr>
              )}
              {items.map(r => (
                <tr key={r.id} className="border-b border-[#CCCCCC] hover:bg-[#E5FFFF]/40">
                  <td className="px-3 py-2.5 font-semibold text-[#011617]">{formatDate(r.next_due)}</td>
                  <td className="px-3 py-2.5"><RenewalBadge daysUntil={r.days_until} /></td>
                  <td className="px-3 py-2.5 text-[#011617]">{clientName(r.client_id)}</td>
                  <td className="px-3 py-2.5 text-[#011617]">{catName(r.task_category_id)}</td>
                  <td className="px-3 py-2.5 text-[#595959]">{r.notes}</td>
                  <td className="px-3 py-2.5 text-right font-bold text-[#011617]">{formatZAR(r.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
